
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import {SweetModal} from "sweet-modal-vue";
import {capitalizeFirst, translate} from "@/helpers/functions";
import {FormWizard, TabContent} from "vue-form-wizard";
import NevronAdvanceTextBox from "@/components/NevronAdvanceTextBox.vue";
import NevronImageField from "@/components/NevronImageField.vue";
import stores from "@/stores";
import TableGeneric from "@/components/NevronTableGeneric.vue";
import TableModalGeneric from "@/components/NevronTableModalGeneric.vue";
import {showToaster} from "@/components/DashboardComposer/helpers";
import _ from 'lodash';
import NevronInput from "@/components/NevronInput.vue";
import AttachItem from "@/modules/Packages/Attachpackageitem.vue";
import NevronIcons from "@/components/NevronIcons.vue";
import {IDeliveryChannel, Message} from "@/stores/Messaging";
import Multiselect from 'vue-multiselect'
import MessageContent from "@/modules/Messaging/MessageContent.vue";
// @ts-ignore
import i18n from '@/i18n.js';

@Component({
  methods: {capitalizeFirst},
  components: {
    MessageContent,
    NevronIcons,
    AttachItem,
    NevronInput,
    TableModalGeneric,
    TableGeneric,
    NevronImageField,
    NevronAdvanceTextBox,
    SweetModal,
    FormWizard,
    TabContent,
    Multiselect,
  }
})
export default class CreateMessage extends Vue {
  message: Message = this.initializeMessage();
  deliveryChannels: IDeliveryChannel[] = [];
  selectedChannels: string[] = [];
  // Utils
  wizardKey = 0;
  stores = stores;
  messageContentRefresh = 0;

  @Prop({default: null})
  item!: IMessage | null;

  get isEditMode() {
    return !!this.item;
  }

  get isMessageScheduled() {
    return this.message.status === 'SCHEDULED';
  }

  get selectedMessageContentKeys(): string[] {
    const uniqueKeys = new Set<string>(); // A Set will only store unique values.

    this.selectedChannels.forEach((channelKey: string) => {
      const channel = this.deliveryChannels.find((ch: IDeliveryChannel) => ch.key === channelKey);
      if (channel?.group) {
        uniqueKeys.add(channel.group); // Use .add method to add items to Set.
      } else {
        uniqueKeys.add(channelKey); // Add channelKey to Set if group is not present.
      }
    });
    return Array.from(uniqueKeys); // Convert Set to Array before returning.
  }


  @Watch('item', {immediate: true})
  onItemChanged() {
    if (this.isEditMode && this.item !== null) {
      this.message = new Message(this.item);
      this.selectedChannels = this.message.deliveryChannels || [];
    }
  }
  async created() {
    this.deliveryChannels = await stores.Messaging.getDeliveryChannels();
  }

  initializeMessage(): Message {
    return new Message({
      status: 'IMMEDIATE',
    });
  }


  onSubmit() {
    if (!this.validateForm('formStep1')) {
      return;
    }
    this.message.deliveryChannels = this.selectedChannels;
    let submitPromise;
    if (this.isEditMode && this.message.id) {
      submitPromise = stores.Messaging.updateItem(this.message.id!, this.message);
    } else {
      submitPromise = stores.Messaging.createNewItem(this.message);
    }

    submitPromise.then((response) => {
      if (response.error) {
        throw new Error(response.error.message);
      }
      showToaster('success', '', Vue.prototype.translate('successfully-updated'));
      const eventToEmit = this.isEditMode ? 'save' : 'created';
      this.$emit(eventToEmit, true);
      this.cancel();
    }).catch((error) => {
      showToaster('error', 'ERROR:', error);
    });
  }

  async saveAsDraft() {
    const isStep1Valid = await this.validateForm('formStep1');
    if (!isStep1Valid && !this.message.name) {
      return;
    }
    this.message.status = 'DRAFT';
    this.onSubmit();
  }

  cancel() {
    // @ts-ignore
    this.$children[0].close();
    this.resetComponent();
  }

  resetComponent() {
    this.message = this.initializeMessage();
    this.selectedChannels = [];
    this.wizardKey += 1;
  }

  validateForm(formRef: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.$forceUpdate();
      this.$nextTick(() => {
        const form = this.$refs[formRef] as HTMLFormElement;
        form.classList.add('was-validated');
        resolve(form.checkValidity());
      });
    });
  }


  attachCustomers(customers: ICustomer[]) {
    const union = customers.concat(this.message.customers.filter((item: ICustomer) => {
      return customers.findIndex((item2: ICustomer) => item.id === item2.id) < 0;
    }));
    this.message.customers = union;
  }

  detachCustomer(recipient: ICustomer) {
    this.message.customers = this.message.customers.filter((item: ICustomer) => item.id !== recipient.id);
  }

  /* Delivery channels START */

  get groupedChannels() {
    return this.deliveryChannels.reduce<Record<string, IDeliveryChannel[]>>((acc, channel) => {
      if (channel.group) {
        if (!acc[channel.group]) acc[channel.group] = [];
        acc[channel.group].push(channel);
      }
      return acc;
    }, {});
  }

  get groupKeys() {
    return Object.keys(this.groupedChannels);
  }

  get standaloneChannels() {
    return this.deliveryChannels.filter(channel => !channel.group);
  }

  toggleGroup(groupKey: string) {
    const channelsInGroup = this.groupedChannels[groupKey].map((ch: IDeliveryChannel) => ch.key);
    if (channelsInGroup.every((key: string) => this.selectedChannels.includes(key))) {
      this.selectedChannels = this.selectedChannels.filter(key => !channelsInGroup.includes(key));
    } else {
      this.selectedChannels = _.uniq([...this.selectedChannels, ...channelsInGroup]);
    }
  }

  isGroupSelected(groupKey: string) {
    const channelsInGroup = this.groupedChannels[groupKey].map((ch: IDeliveryChannel) => ch.key);
    return channelsInGroup.every((key: string) => this.selectedChannels.includes(key));
  }

  /* Delivery channels END */


  oneMinuteFromNow() {
    const future = new Date();
    future.setMinutes(future.getMinutes() + 1); // adding 1 minute to the current time

    const year = future.getFullYear();
    const month = String(future.getMonth() + 1).padStart(2, '0'); // month is 0-based, so +1 is needed
    const day = String(future.getDate()).padStart(2, '0');
    const hours = String(future.getHours()).padStart(2, '0');
    const minutes = String(future.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  attachDeviceGroups(deviceGroups: IDeviceGroup[]) {
    const union = deviceGroups.concat(this.message.deviceGroups.filter((item: IDeviceGroup) => {
      return deviceGroups.findIndex((item2: IDeviceGroup) => item.id === item2.id) < 0;
    }));
    this.message.deviceGroups = union;
  }

  detachDeviceGroup(deviceGroup: IDeviceGroup) {
    this.message.deviceGroups = this.message.deviceGroups.filter((item: IDeviceGroup) => item.id !== deviceGroup.id);
  }

  messageContentByType(type: string): IMessageContent {
    return this.message.messageContents.find(mc => mc.type === type)
      || {'messageId': this.message.id || undefined, type} as IMessageContent;
  }

  updateMessageContentByType(messageContent: IMessageContent) {
    const type = messageContent.type;
    const index = this.message.messageContents.findIndex(mc => mc.type === type);
    if (index >= 0) {
      this.message.messageContents.splice(index, 1, messageContent);
    } else {
      this.message.messageContents.push(messageContent);
    }
  }


  getContentTypeFromDeliveryChannel(channelKey: string): string {
    const channel = this.deliveryChannels.find((ch: IDeliveryChannel) => ch.key === channelKey);
    return channel?.group || channelKey;
  }

  copyContentToOtherDevices(deliveryChannelType: string) {
    const fromType = this.getContentTypeFromDeliveryChannel(deliveryChannelType);
    const messageContentFrom = this.messageContentByType(fromType);

    if (!messageContentFrom) {
      return;
    }

    this.message.messageContents = this.message.messageContents.map(content => {
      if (content.type === fromType) {
        return content;
      }

      const a: IMessageContent = {
        ...content,
        title: this.copyText(messageContentFrom.title) || content.title,
        text: this.copyText(messageContentFrom.text) || content.text,
        imageId: !!messageContentFrom.imageId ? messageContentFrom.imageId : content.imageId,
        image: messageContentFrom.image || content.image,
        hasButton: (!['SMS', 'EMAIL'].includes(content.type) && messageContentFrom.hasButton !== undefined) ? messageContentFrom.hasButton : content.hasButton,
        buttonText: messageContentFrom.buttonText || content.buttonText,
        linkedItemId: messageContentFrom.linkedItemId || content.linkedItemId,
        linkedItemType: messageContentFrom.linkedItemType || content.linkedItemType,
        linkedItem: messageContentFrom.linkedItem || content.linkedItem,
        iconFilename: messageContentFrom.iconFilename || content.iconFilename
      };
      return a;
    });
    this.messageContentRefresh += 1;
  }

  copyText(text: string | undefined): string | undefined {
    if (!text) {
      return undefined;
    }
    if (i18n.te(text)) {
      return Vue.prototype.translate(text);
    } else {
      return text;
    }
  }

}
